import type { AdminAsset, AdminPageBlock, ShootSummary, SocialLink } from '@diana/contracts';
import { ExternalLink, GripVertical, Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { CompactRichTextField } from '../../components/CompactRichTextField.js';
import { plainToRichText } from '../../components/RichTextField.js';
import { SortableList } from '../../components/SortableList.js';
import { Button, Checkbox, Popover } from '../../ui/index.js';
import { ContextualMediaField } from '../media/ContextualMediaField.js';
import { TextAreaField, TextField } from '../settings/SettingsEditor.js';
import './HomepageEditor.css';
import { PageSection, PageSectionsWorkspace } from './PageSectionsWorkspace.js';

type HeroBlock = Extract<AdminPageBlock, { type: 'Hero' }>;
type AboutBlock = Extract<AdminPageBlock, { type: 'EditorialText' }>;
type PortfolioBlock = Extract<AdminPageBlock, { type: 'Gallery' }>;
type PricingBlock = Extract<AdminPageBlock, { type: 'Pricing' }>;
type ProcessBlock = Extract<AdminPageBlock, { type: 'Process' }>;
type ContactBlock = Extract<AdminPageBlock, { type: 'CTA' }>;
type SectionId = 'hero' | 'about' | 'portfolio' | 'pricing' | 'gift' | 'process' | 'contact';

export function HomepageEditor({
  blocks,
  assets,
  shoots,
  socialLinks,
  onChange,
}: {
  blocks: readonly AdminPageBlock[];
  assets: readonly AdminAsset[];
  shoots: readonly ShootSummary[];
  socialLinks: readonly SocialLink[];
  onChange(block: AdminPageBlock): void;
}) {
  const [expanded, setExpanded] = useState<SectionId | null>(null);
  const hero = blockOfType(blocks, 'Hero');
  const about = blockOfType(blocks, 'EditorialText');
  const portfolio = blockOfType(blocks, 'Gallery');
  const pricing = blockOfType(blocks, 'Pricing');
  const process = blockOfType(blocks, 'Process');
  const contact = blockOfType(blocks, 'CTA');

  return (
    <PageSectionsWorkspace>
      <HomeSection
        id="hero"
        index="01"
        title="Hero slider"
        description="Choose which public galleries appear in the opening slider."
        expanded={expanded === 'hero'}
        onToggle={() => setExpanded(expanded === 'hero' ? null : 'hero')}
      >
        {hero ? (
          <HeroShootSelector block={hero} shoots={shoots} onChange={onChange} />
        ) : (
          <MissingSection />
        )}
      </HomeSection>

      <HomeSection
        id="about"
        index="02"
        title="About"
        description="A protected layout with one heading, one accent text, one body and two images."
        expanded={expanded === 'about'}
        onToggle={() => setExpanded(expanded === 'about' ? null : 'about')}
      >
        {about ? (
          <AboutFields block={about} assets={assets} onChange={onChange} />
        ) : (
          <MissingSection />
        )}
      </HomeSection>

      <HomeSection
        id="portfolio"
        index="03"
        title="Portfolio"
        description="This section follows Portfolio stories and is not duplicated on the Homepage."
        expanded={expanded === 'portfolio'}
        onToggle={() => setExpanded(expanded === 'portfolio' ? null : 'portfolio')}
        source="Linked content"
      >
        {portfolio ? <PortfolioFields block={portfolio} onChange={onChange} /> : <MissingSection />}
      </HomeSection>

      <HomeSection
        id="pricing"
        index="04"
        title="Pricing"
        description="Packages and prices use the shared Pricing catalog."
        expanded={expanded === 'pricing'}
        onToggle={() => setExpanded(expanded === 'pricing' ? null : 'pricing')}
        source="Linked content"
      >
        {pricing ? <PricingFields block={pricing} onChange={onChange} /> : <MissingSection />}
      </HomeSection>

      <HomeSection
        id="gift"
        index="05"
        title="Gift photoshoot"
        description="Edit the small promotional block shown after Pricing."
        expanded={expanded === 'gift'}
        onToggle={() => setExpanded(expanded === 'gift' ? null : 'gift')}
      >
        {pricing ? <GiftFields block={pricing} onChange={onChange} /> : <MissingSection />}
      </HomeSection>

      <HomeSection
        id="process"
        index="06"
        title="How it works"
        description="Edit the heading and maintain a short ordered list of process steps."
        expanded={expanded === 'process'}
        onToggle={() => setExpanded(expanded === 'process' ? null : 'process')}
      >
        {process ? <ProcessFields block={process} onChange={onChange} /> : <MissingSection />}
      </HomeSection>

      <HomeSection
        id="contact"
        index="07"
        title="Contact"
        description="Edit the heading and button label, then choose which global social links appear."
        expanded={expanded === 'contact'}
        onToggle={() => setExpanded(expanded === 'contact' ? null : 'contact')}
      >
        {contact ? (
          <ContactFields block={contact} socialLinks={socialLinks} onChange={onChange} />
        ) : (
          <MissingSection />
        )}
      </HomeSection>
    </PageSectionsWorkspace>
  );
}

function PortfolioFields({
  block,
  onChange,
}: {
  block: PortfolioBlock;
  onChange(block: PortfolioBlock): void;
}) {
  return (
    <div className="home-fields">
      <TextField
        label="Section heading"
        value={block.payload.heading ?? ''}
        placeholder="Stories by genre"
        onChange={(heading) =>
          onChange({
            ...block,
            payload: { ...block.payload, heading: heading || undefined },
          })
        }
      />
      <LinkedSection
        title="Portfolio content is managed separately"
        body="Homepage cards are taken from the Portfolio gallery."
        href="/pages"
        action="Open Pages"
      />
    </div>
  );
}

function PricingFields({
  block,
  onChange,
}: {
  block: PricingBlock;
  onChange(block: PricingBlock): void;
}) {
  return (
    <div className="home-fields">
      <TextField
        label="Section heading"
        value={block.payload.heading ?? ''}
        placeholder="Choose your story"
        onChange={(heading) =>
          onChange({
            ...block,
            payload: { ...block.payload, heading: heading || undefined },
          })
        }
      />
      <LinkedSection
        title="Packages stay in one source of truth"
        body="Changes to packages, features and prices automatically flow to the Homepage."
        href="/packages"
        action="Open Packages"
      />
    </div>
  );
}

function HomeSection(props: {
  id: SectionId;
  index: string;
  title: string;
  description: string;
  expanded: boolean;
  source?: string;
  onToggle(): void;
  children: ReactNode;
}) {
  return <PageSection {...props} id={`home-section-${props.id}`} />;
}

function HeroShootSelector({
  block,
  shoots,
  onChange,
}: {
  block: HeroBlock;
  shoots: readonly ShootSummary[];
  onChange(block: HeroBlock): void;
}) {
  const [galleryPickerOpen, setGalleryPickerOpen] = useState(false);
  const activeShoots = shoots.filter((shoot) => shoot.siteAccess === 'Public');
  const selectedIds = linkedShootIds(block, shoots).filter((id) =>
    activeShoots.some((shoot) => shoot.id === id),
  );
  const selectedShoots = selectedIds
    .map((id) => activeShoots.find((shoot) => shoot.id === id))
    .filter((shoot): shoot is ShootSummary => Boolean(shoot));
  const availableShoots = activeShoots.filter((shoot) => !selectedIds.includes(shoot.id));
  const linkedAssetIds = new Set(shoots.map((shoot) => shoot.payload.cover.assetId));
  const legacySlides =
    block.payload.slides?.filter(
      (slide) => !slide.shootId && !linkedAssetIds.has(slide.media.assetId),
    ).length ?? 0;

  if (activeShoots.length === 0) {
    return (
      <LinkedSection
        title="Make a gallery public first"
        body="The Homepage slider is filled from public galleries and their cover images."
        href="/shoots"
        action="Open Galleries"
      />
    );
  }

  const updateSelection = (nextIds: readonly string[]) => {
    const slides = nextIds
      .map((id) => activeShoots.find((item) => item.id === id))
      .filter((item): item is ShootSummary => Boolean(item))
      .map((item) => ({
        shootId: item.id,
        label: item.payload.title,
        media: item.payload.cover,
      }));
    onChange({
      ...block,
      payload: {
        ...block.payload,
        media: undefined,
        slides,
      },
    });
  };

  return (
    <div className="home-shoot-selector">
      <div className="home-field-intro">
        <div>
          <strong>Featured galleries</strong>
          <p>Add active galleries, then drag the selected ones into the order you want.</p>
        </div>
        <Link className="text-action" to="/shoots">
          Manage galleries <ExternalLink size={14} aria-hidden="true" />
        </Link>
      </div>
      {legacySlides > 0 ? (
        <p className="inline-note">
          {legacySlides} existing slider item{legacySlides === 1 ? '' : 's'} are not linked to a
          gallery yet. The first selection change replaces them with the galleries selected below.
        </p>
      ) : null}
      <section className="home-gallery-picker" aria-labelledby="selected-hero-galleries">
        <header>
          <strong id="selected-hero-galleries">Selected galleries</strong>
          {availableShoots.length > 0 ? (
            <Popover
              title="Add gallery"
              open={galleryPickerOpen}
              onOpenChange={setGalleryPickerOpen}
              trigger={
                <Button size="small" leadingIcon={Plus} aria-label="Add gallery to Hero slider">
                  Add gallery
                </Button>
              }
            >
              <div className="home-gallery-menu">
                {availableShoots.map((shoot) => (
                  <button
                    className="home-gallery-option"
                    type="button"
                    key={shoot.id}
                    aria-label={`Add ${shoot.payload.title} to Hero slider`}
                    onClick={() => {
                      updateSelection([...selectedIds, shoot.id]);
                      setGalleryPickerOpen(false);
                    }}
                  >
                    <GalleryCover shoot={shoot} />
                    <GallerySummary shoot={shoot} />
                    <Plus size={17} aria-hidden="true" />
                  </button>
                ))}
              </div>
            </Popover>
          ) : (
            <Button size="small" leadingIcon={Plus} disabled>
              Add gallery
            </Button>
          )}
        </header>
        {selectedShoots.length > 0 ? (
          <SortableList
            items={selectedShoots}
            label={(shoot) => shoot.payload.title}
            onChange={(next) => updateSelection(next.map((shoot) => shoot.id))}
            render={(shoot) => (
              <div className="home-gallery-selected">
                <GalleryCover shoot={shoot} />
                <GallerySummary shoot={shoot} />
                <button
                  type="button"
                  aria-label={`Remove ${shoot.payload.title} from Hero slider`}
                  disabled={selectedShoots.length === 1}
                  onClick={() => updateSelection(selectedIds.filter((id) => id !== shoot.id))}
                >
                  <Trash2 size={16} aria-hidden="true" />
                </button>
              </div>
            )}
          />
        ) : (
          <p className="home-gallery-picker__empty">Choose a gallery to create the slider.</p>
        )}
      </section>
      <CompactRichTextField
        label="Text below slider"
        value={block.payload.subtitle ?? plainToRichText('')}
        onChange={(subtitle) => onChange({ ...block, payload: { ...block.payload, subtitle } })}
      />
    </div>
  );
}

function GalleryCover({ shoot }: { shoot: ShootSummary }) {
  return (
    <img
      className="home-gallery-cover"
      src={`/api/v1/admin/assets/${shoot.payload.cover.assetId}/preview`}
      alt=""
    />
  );
}

function GallerySummary({ shoot }: { shoot: ShootSummary }) {
  return (
    <span className="home-gallery-summary">
      <strong>{shoot.payload.title}</strong>
      <small>{shoot.payload.subtitle ?? shoot.route}</small>
    </span>
  );
}

function AboutFields({
  block,
  assets,
  onChange,
}: {
  block: AboutBlock;
  assets: readonly AdminAsset[];
  onChange(block: AboutBlock): void;
}) {
  const payload = block.payload;
  const update = (patch: Partial<typeof payload>) =>
    onChange({ ...block, payload: { ...payload, ...patch } });
  const updateMedia = (index: number, media: NonNullable<typeof payload.media>[number]) => {
    const next = [...(payload.media ?? [])];
    next[index] = media;
    update({ media: next.filter(Boolean).slice(0, 2) });
  };

  return (
    <div className="home-fields">
      <TextField
        label="Section heading"
        value={payload.heading ?? ''}
        onChange={(heading) => update({ heading: heading || undefined })}
      />
      <div className="form-grid form-grid--two">
        <CompactRichTextField
          label="Large accent text"
          value={payload.body}
          onChange={(body) => update({ body })}
        />
        <CompactRichTextField
          label="Body text"
          value={payload.aside ?? plainToRichText('')}
          onChange={(aside) => update({ aside })}
        />
      </div>
      <div className="form-grid form-grid--two">
        <ContextualMediaField
          title="About image 1"
          profile="compact"
          cropProfile="portrait-3-4"
          description="First portrait in the Homepage About composition."
          value={payload.media?.[0]}
          assets={assets}
          onChange={(media) => updateMedia(0, media)}
        />
        <ContextualMediaField
          title="About image 2"
          profile="compact"
          cropProfile="portrait-3-4"
          description="Second portrait in the Homepage About composition."
          value={payload.media?.[1]}
          assets={assets}
          onChange={(media) => updateMedia(1, media)}
        />
      </div>
    </div>
  );
}

function GiftFields({
  block,
  onChange,
}: {
  block: PricingBlock;
  onChange(block: PricingBlock): void;
}) {
  const payload = block.payload;
  const gift = payload.gift ?? {
    heading: 'Gift a photoshoot',
    body: plainToRichText('A thoughtful photography experience for someone important.'),
    actionLabel: 'Choose a certificate',
  };
  const update = (patch: Partial<typeof gift>) =>
    onChange({ ...block, payload: { ...payload, gift: { ...gift, ...patch } } });

  return (
    <div className="home-fields">
      <TextField
        label="Gift heading"
        value={gift.heading}
        onChange={(heading) => update({ heading })}
      />
      <CompactRichTextField
        label="Supporting text"
        value={gift.body}
        onChange={(body) => update({ body })}
      />
      <TextField
        label="Button label"
        value={gift.actionLabel}
        onChange={(actionLabel) => update({ actionLabel })}
      />
      <p className="inline-note">
        The button destination is protected and always opens Gift certificates.
      </p>
    </div>
  );
}

function ProcessFields({
  block,
  onChange,
}: {
  block: ProcessBlock;
  onChange(block: ProcessBlock): void;
}) {
  if (block.payload.source === 'Pricing') {
    return (
      <LinkedSection
        title="This process follows Pricing"
        body="Switching to page-specific process steps is required before editing them here."
        href="/packages"
        action="Open Pricing"
      />
    );
  }
  const payload = block.payload;
  const updateItems = (items: typeof payload.items) =>
    onChange({
      ...block,
      payload: {
        ...payload,
        items: items.map((item, index) => ({
          ...item,
          eyebrow: String(index + 1).padStart(2, '0'),
        })),
      },
    });
  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= payload.items.length) return;
    const items = [...payload.items];
    [items[index], items[target]] = [items[target]!, items[index]!];
    updateItems(items);
  };

  return (
    <div className="home-fields">
      <TextField
        label="Section heading"
        value={payload.heading ?? ''}
        onChange={(heading) =>
          onChange({ ...block, payload: { ...payload, heading: heading || undefined } })
        }
      />
      <div className="home-process-list">
        {payload.items.map((item, index) => (
          <article className="home-process-item" key={item.key}>
            <header>
              <span>
                <GripVertical size={16} aria-hidden="true" />
                Step {String(index + 1).padStart(2, '0')}
              </span>
              <span className="home-process-item__actions">
                <button
                  type="button"
                  aria-label={`Move ${item.title} up`}
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  Up
                </button>
                <button
                  type="button"
                  aria-label={`Move ${item.title} down`}
                  disabled={index === payload.items.length - 1}
                  onClick={() => move(index, 1)}
                >
                  Down
                </button>
                <button
                  type="button"
                  aria-label={`Remove ${item.title}`}
                  disabled={payload.items.length === 1}
                  onClick={() =>
                    updateItems(payload.items.filter((_, itemIndex) => itemIndex !== index))
                  }
                >
                  <Trash2 size={14} aria-hidden="true" />
                  Remove
                </button>
              </span>
            </header>
            <div className="form-grid form-grid--two">
              <TextField
                label={`Step ${index + 1} title`}
                value={item.title}
                onChange={(title) => {
                  const items = [...payload.items];
                  items[index] = { ...item, title };
                  updateItems(items);
                }}
              />
              <CompactRichTextField
                label={`Step ${index + 1} text`}
                value={item.body}
                onChange={(body) => {
                  const items = [...payload.items];
                  items[index] = { ...item, body };
                  updateItems(items);
                }}
              />
            </div>
          </article>
        ))}
      </div>
      <Button
        variant="secondary"
        leadingIcon={Plus}
        disabled={payload.items.length >= 6}
        onClick={() =>
          updateItems([
            ...payload.items,
            {
              key: crypto.randomUUID(),
              title: 'New step',
              body: plainToRichText('Describe what happens at this stage.'),
            },
          ])
        }
      >
        Add step
      </Button>
      <p className="inline-note">
        Use between one and six steps so the public layout remains balanced.
      </p>
    </div>
  );
}

function ContactFields({
  block,
  socialLinks,
  onChange,
}: {
  block: ContactBlock;
  socialLinks: readonly SocialLink[];
  onChange(block: ContactBlock): void;
}) {
  const payload = block.payload;
  const selected = payload.visibleSocialLinks ?? socialLinks.map((link) => link.href);
  const update = (patch: Partial<typeof payload>) =>
    onChange({ ...block, payload: { ...payload, ...patch } });

  return (
    <div className="home-fields">
      <div className="form-grid form-grid--two">
        <TextAreaField
          label="Contact heading"
          value={payload.heading}
          onChange={(heading) => update({ heading })}
        />
        <TextField
          label="Button label"
          value={payload.action.label}
          onChange={(label) => update({ action: { ...payload.action, label } })}
        />
      </div>
      <div className="home-socials">
        <div className="home-field-intro">
          <div>
            <strong>Social links shown here</strong>
            <p>The addresses themselves are managed once in Site settings.</p>
          </div>
          <Link className="text-action" to="/settings">
            Open Settings <ExternalLink size={14} aria-hidden="true" />
          </Link>
        </div>
        {socialLinks.length ? (
          <div className="home-choice-list home-choice-list--compact">
            {socialLinks.map((link) => (
              <div className="home-choice" key={link.href}>
                <Checkbox
                  label={link.label}
                  description={link.handle}
                  checked={selected.includes(link.href)}
                  onChange={(event) => {
                    const visibleSocialLinks = event.currentTarget.checked
                      ? [...selected, link.href]
                      : selected.filter((href) => href !== link.href);
                    update({ visibleSocialLinks });
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          <p className="inline-note">No global social links have been added yet.</p>
        )}
      </div>
    </div>
  );
}

function LinkedSection({
  title,
  body,
  href,
  action,
}: {
  title: string;
  body: string;
  href: string;
  action: string;
}) {
  return (
    <div className="home-linked">
      <div>
        <strong>{title}</strong>
        <p>{body}</p>
      </div>
      <Link className="button button--quiet" to={href}>
        {action}
        <ExternalLink size={14} aria-hidden="true" />
      </Link>
    </div>
  );
}

function MissingSection() {
  return (
    <p className="inline-note" role="status">
      This protected section is missing from the current page data.
    </p>
  );
}

function blockOfType<Type extends AdminPageBlock['type']>(
  blocks: readonly AdminPageBlock[],
  type: Type,
): Extract<AdminPageBlock, { type: Type }> | undefined {
  return blocks.find(
    (block): block is Extract<AdminPageBlock, { type: Type }> => block.type === type,
  );
}

function linkedShootIds(block: HeroBlock, shoots: readonly ShootSummary[]) {
  const byCover = new Map(shoots.map((shoot) => [shoot.payload.cover.assetId, shoot.id]));
  return (
    block.payload.slides
      ?.map((slide) => slide.shootId ?? byCover.get(slide.media.assetId))
      .filter((id): id is string => Boolean(id)) ?? []
  );
}
